import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLanguage } from '@context'
import { Coords, getCurrentPosition } from '@utils'
import { claimLocationShare, releaseLocationShare } from '../utils/dailyLocation'
import { reverseGeocode } from '../utils/reverseGeocode'
import { useFtUpdateLocation } from './api/mutations/ft-location/useFtUpdateLocation'
import type { Profile } from './api/queries/useGetProfile'

// One save at a time for the whole app: screens opened together (the web
// layout and the map, for instance) must not both send the position.
let isSharing = false

/**
 * Saves my position on the server, with its city and country, at most once a
 * day per account (see dailyLocation). Login, the map, "Contacts à proximité"
 * and the web layout all go through it. Returns whether a save happened.
 *
 * Callers keep their own privacy check: never call it for someone invisible.
 */
export function useShareMyLocation() {
  const { mutateAsync } = useFtUpdateLocation()
  const queryClient = useQueryClient()
  const { currentLanguage } = useLanguage()

  return useCallback(
    async (accountId: string | undefined, knownPosition?: Coords | null): Promise<boolean> => {
      const save = async (): Promise<boolean> => {
        const position = knownPosition ?? (await getCurrentPosition())
        if (!position) return false
        const { city, country } = await reverseGeocode(position.latitude, position.longitude)
        const result = await mutateAsync({
          latitude: position.latitude,
          longitude: position.longitude,
          city,
          country,
        })
        // The endpoint answers { success: false } instead of an error status.
        return result?.success !== false
      }

      if (!accountId || isSharing) return false
      isSharing = true
      try {
        // My profile when it is already loaded (never fetched here: the login
        // screen uses this hook too). It tells whether another device saved
        // my position today. On the web it first comes back from the
        // browser's storage, maybe from another day: a reload already under
        // way is awaited (cancelRefetch: false joins it, no request is added).
        const profileKey = ['profile', 'me', currentLanguage]
        if (queryClient.isFetching({ queryKey: profileKey, exact: true }) > 0) {
          await queryClient.refetchQueries({ queryKey: profileKey, exact: true }, { cancelRefetch: false })
        }
        const me = queryClient.getQueryData<Profile>(profileKey)
        const serverLastSharedAt = me?.accountId === accountId ? me.ft_lastLocationAt : undefined
        if (!(await claimLocationShare(accountId, new Date(), serverLastSharedAt))) return false
        const saved = await save().catch(() => false)
        // No position (location refused) or a failed save: another screen may
        // try again today.
        if (!saved) await releaseLocationShare(accountId)
        // An open "Contacts à proximité" page was computed without this
        // position: it reloads (nothing is fetched when it is not on screen).
        else void queryClient.invalidateQueries({ queryKey: ['ft-nearby-contacts'] })
        return saved
      } finally {
        isSharing = false
      }
    },
    [mutateAsync, queryClient, currentLanguage],
  )
}
